import React, { useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import api from '../services/api';
import { DollarSign, Info } from 'lucide-react';

const HookItUpButton = ({ product, variant }) => {
  const [open, setOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    offer_amount: '',
    customer_name: '',
    customer_email: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.offer_amount || parseFloat(formData.offer_amount) <= 0) {
      toast.error('Please enter a valid offer amount');
      return;
    }
    
    if (!formData.customer_name.trim() || !formData.customer_email.trim()) {
      toast.error('Please provide your name and email');
      return;
    }

    try {
      setLoading(true);
      
      const offerData = {
        product_id: product.id,
        product_name: product.name,
        product_handle: product.handle,
        original_price: variant?.price || product.variants?.[0]?.price || 0,
        offer_amount: parseFloat(formData.offer_amount),
        message: `Offer submitted for ${product.name}`,
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        variant_id: variant?.id || product.variants?.[0]?.id
      };

      const response = await api.post('/offers/submit', offerData);
      
      if (response.data.success) {
        toast.success('Your offer has been submitted! We\'ll get back to you soon at ' + formData.customer_email);
        setOpen(false);
        setFormData({
          offer_amount: '',
          customer_name: '',
          customer_email: ''
        });
      }
    } catch (error) {
      console.error('Error submitting offer:', error);
      toast.error('Failed to submit offer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const originalPrice = variant?.price || product.variants?.[0]?.price || 0;

  return (
    <>
      {/* Hook It Up Button */}
      <Button
        onClick={() => setOpen(true)}
        className="bg-green-600 hover:bg-green-700 text-white font-black text-sm sm:text-base py-4 sm:py-6 uppercase tracking-wider border-4 border-charcoal transition-all w-full flex items-center justify-center gap-1 sm:gap-2"
      >
        <DollarSign className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
        <span className="truncate">HOOK IT UP</span>
      </Button>

      {/* Make Offer Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-charcoal">Hook It Up - Make an Offer</DialogTitle>
            <DialogDescription className="text-warmGray">
              {product.name}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            {/* Original Price Display */}
            <div className="bg-gray-100 p-3 rounded">
              <p className="text-sm text-gray-600">Current Price</p>
              <p className="text-2xl font-bold text-charcoal">${originalPrice.toFixed(2)}</p>
            </div>

            {/* Offer Amount */}
            <div>
              <Label htmlFor="offer_amount" className="text-charcoal font-bold">Your Offer Amount *</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <Input
                  id="offer_amount"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={formData.offer_amount}
                  onChange={(e) => setFormData({...formData, offer_amount: e.target.value})}
                  className="pl-7"
                  required
                />
              </div>
            </div>

            {/* Name */}
            <div>
              <Label htmlFor="customer_name" className="text-charcoal font-bold">Your Name *</Label>
              <Input
                id="customer_name"
                type="text"
                placeholder="John Doe"
                value={formData.customer_name}
                onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                className="mt-1"
                required
              />
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="customer_email" className="text-charcoal font-bold">Your Email *</Label>
              <Input
                id="customer_email"
                type="email"
                placeholder="john@example.com"
                value={formData.customer_email}
                onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                className="mt-1"
                required
              />
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold"
            >
              {loading ? 'Submitting...' : 'Submit Offer'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Info Dialog */}
      <Dialog open={infoOpen} onOpenChange={setInfoOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-charcoal">What's "Hook It Up"?</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-warmGray">
            <p>
              <strong className="text-charcoal">Hook It Up</strong> is our way of making luxury fashion more accessible. 
              We believe in building relationships with our customers, not just transactions.
            </p>
            
            <div>
              <h3 className="font-bold text-charcoal mb-2">How It Works:</h3>
              <ol className="list-decimal list-inside space-y-2">
                <li>Make an offer on any item you love</li>
                <li>Tell us why that price works for you</li>
                <li>We review every offer personally</li>
                <li>If we can make it work, you'll get a special checkout link</li>
              </ol>
            </div>

            <div>
              <h3 className="font-bold text-charcoal mb-2">Our Promise:</h3>
              <ul className="list-disc list-inside space-y-2">
                <li>Every offer is reviewed by our team</li>
                <li>We respond within 24-48 hours</li>
                <li>No automated rejections - we consider each request</li>
                <li>Sometimes we can meet your price, sometimes we counter</li>
              </ul>
            </div>

            <p className="text-sm italic">
              Built on perseverance. Built for you. Let's work something out.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default HookItUpButton;
